import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { FiPlus, FiTrash2, FiSearch, FiUpload, FiMail } from 'react-icons/fi';
import { useConfirm } from '../../ui/ConfirmDialog';

const sourceBadge = {
  manual: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  imported: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  team_user: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  stalled_signup: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  unsubscribe: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
};

const Contacts = () => {
  const confirm = useConfirm();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [total, setTotal] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', firstName: '', lastName: '' });
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/emails/contacts', {
        params: { search, limit: 200 }
      });
      setContacts(r.data.contacts);
      setTotal(r.data.pagination.total);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    load();
  }, [load]);

  const addContact = async (e) => {
    e.preventDefault();
    if (!addForm.email.trim()) return;
    try {
      const r = await axios.post('/api/admin/emails/contacts', addForm);
      toast.success(r.data.created ? 'Contact added' : 'Contact updated');
      setAddForm({ email: '', firstName: '', lastName: '' });
      setShowAdd(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add contact');
    }
  };

  const deleteContact = async (id) => {
    const ok = await confirm({
      title: 'Delete this contact?',
      description: 'They will also be removed from any lists.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/admin/emails/contacts/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      toast.success('Contact deleted');
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      const r = await axios.post('/api/admin/emails/contacts/import', { rows });
      toast.success(`Imported ${r.data.added} new, ${r.data.updated} updated, ${r.data.skipped} skipped`);
      load();
    } catch (error) {
      console.error('CSV import failed:', error);
      toast.error('CSV import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
          />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {total.toLocaleString()} total
        </span>
        <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
          <FiUpload className="w-3.5 h-3.5" />
          {importing ? 'Importing…' : 'Import CSV'}
          <input type="file" accept=".csv,text/csv" onChange={handleCsvImport} className="hidden" disabled={importing} />
        </label>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-xs font-medium transition-colors"
        >
          <FiPlus className="w-3.5 h-3.5" />
          Add contact
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addContact} className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              type="email"
              required
              placeholder="email@example.com"
              value={addForm.email}
              onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
            />
            <input
              type="text"
              placeholder="First name"
              value={addForm.firstName}
              onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
              className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
            />
            <input
              type="text"
              placeholder="Last name"
              value={addForm.lastName}
              onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
              className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
            />
            <button
              type="submit"
              className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              Add
            </button>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
            CSV import expects a header row with <code>email,first_name,last_name</code> (or firstName/lastName).
          </p>
        </form>
      )}

      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading contacts…</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="p-10 text-center">
          <FiMail className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search ? 'No contacts match your search.' : 'No contacts yet. Add one or import a CSV to get started.'}
          </p>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Email</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Name</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Source</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Added</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {contacts.map(c => (
              <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td className="px-4 py-2 text-xs text-gray-900 dark:text-white">{c.email}</td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${sourceBadge[c.source] || sourceBadge.manual}`}>
                    {c.source}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                  {formatDistanceToNow(new Date(c.created_at || c.createdAt), { addSuffix: true })}
                </td>
                <td className="px-4 py-2">
                  {c.unsubscribedAt || c.unsubscribed_at ? (
                    <span className="text-xs text-red-600 dark:text-red-400">Unsubscribed</span>
                  ) : (
                    <span className="text-xs text-emerald-600 dark:text-emerald-400">Active</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => deleteContact(c.id)}
                    className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                    title="Delete contact"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Minimal CSV parser — handles quoted fields and comma-escape.
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length === 0) return [];

  const header = splitCsvLine(lines[0]).map(h => h.trim().toLowerCase());
  const emailIdx = header.findIndex(h => h === 'email');
  const firstIdx = header.findIndex(h => h === 'first_name' || h === 'firstname');
  const lastIdx = header.findIndex(h => h === 'last_name' || h === 'lastname');

  if (emailIdx === -1) return [];

  return lines.slice(1).map(line => {
    const cells = splitCsvLine(line);
    return {
      email: (cells[emailIdx] || '').trim(),
      firstName: firstIdx >= 0 ? (cells[firstIdx] || '').trim() : undefined,
      lastName: lastIdx >= 0 ? (cells[lastIdx] || '').trim() : undefined
    };
  });
}

function splitCsvLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' && line[i + 1] === '"') { current += '"'; i++; }
    else if (c === '"') inQuotes = !inQuotes;
    else if (c === ',' && !inQuotes) { cells.push(current); current = ''; }
    else current += c;
  }
  cells.push(current);
  return cells;
}

export default Contacts;
